import { isDefined, OrderWarningsResponse, TempOrder } from '@eih-netbroker/common';
import { useCallback } from 'react';
import { useDialog } from '../../../../context/DialogContext';
import { Config } from '../../../../shared/config/config';
import { useAccounts } from '../../../../shared/services/useAccounts';
import { OrderModal2Wrapper } from '../../components/order-new/OrderModal2Wrapper';
import { OrderFormData } from '../../components/order/OrderForm';
import { OrderModal, OrderModalProps } from '../../components/order/OrderModal';
import { OrderCurrencyExchangeStateParams } from '../../components/OrderCurrencyExchangeStateContext';
import { OrderStateProvider } from '../../components/OrderStateContext';
import { OrderFormSourceComponent } from '../../types/orderTypes';

export const useOrderModal = () => {
  const { getSelectedAccount } = useAccounts();
  const { openDialog } = useDialog();

  /**
   * Displays the order modal with the given order data.
   * If there is no single account selected, the user will be prompted to select one.
   */
  const openOrderModal = useCallback(
    async (order?: OrderFormData, modalProps?: Partial<OrderModalProps>, transactionId?: string): Promise<void> => {
      const account = await getSelectedAccount();
      if (!isDefined(account)) {
        return;
      }
      return await openDialog<void>((close: () => void) => (
        <OrderStateProvider initialTransactionId={transactionId}>
          {Config.FeatureFlags.isNewOrderFormEnabled ? (
            <OrderModal2Wrapper
              {...modalProps}
              initOrderFormData={order}
              onCancel={close}
            />
          ) : (
            <OrderModal
              {...modalProps}
              order={order}
              onCancel={() => close()}
            />
          )}
        </OrderStateProvider>
      ));
    },
    [getSelectedAccount, openDialog],
  );

  const openOrderModalWithTempOrder = useCallback(
    async (
      order?: OrderFormData,
      modalProps?: Partial<OrderModalProps>,
      tempOrder?: TempOrder,
      warnings?: OrderWarningsResponse,
      transactionId?: string,
      isWithCurrencyExchange?: boolean,
      orderCurrencyExchangeStateParams?: OrderCurrencyExchangeStateParams,
    ): Promise<void> => {
      return await openDialog<void>((close: () => void) => (
        <OrderStateProvider
          initialTransactionId={transactionId}
          tempOrder={tempOrder}
          warnings={warnings?.messages}
          orderCurrencyExchangeStateParams={orderCurrencyExchangeStateParams}
        >
          {Config.FeatureFlags.isNewOrderFormEnabled ? (
            <OrderModal2Wrapper
              {...modalProps}
              initOrderFormData={order}
              onCancel={shouldResetFormToDefaults => {
                modalProps?.onCancel?.(shouldResetFormToDefaults);
                close();
              }}
              sourceComponent={OrderFormSourceComponent.Widget}
              isWithCurrencyExchange={isWithCurrencyExchange}
            />
          ) : (
            <OrderModal
              {...modalProps}
              order={order}
              onCancel={isCreated => {
                modalProps?.onCancel?.(isCreated);
                close();
              }}
              isWithCurrencyExchange={isWithCurrencyExchange}
            />
          )}
        </OrderStateProvider>
      ));
    },
    [openDialog],
  );

  return {
    openOrderModal,
    openOrderModalWithTempOrder,
  };
};
